import { CenteredContent } from '@/shared/ui/layout/CenteredContent';
import { SectionWrapper } from '@/entities/widget';
import { FourCsWidgetSchemaType } from '@inference.engine/widgets';
import { BorderedBox } from '@/shared/ui/BorderedBox';
import { FOUR_CS_KEYS } from '../../model/four-cs-widget.constants';
import CheckCircleImage from '@/shared/assets/images/CheckCircleGrey.svg';
import CheckCircleOrangeImage from '@/shared/assets/images/CheckCircleOrange.svg';
import { OptimizedImage } from '@/shared/ui/OptimizedImage';
import { Badge, Checkbox } from '@monksflow/monks-ui';
import { useSidePanelScroll } from '@/shared/ui/SidePanelWrapper/SidePanelScrollContext';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

// Exclude confidence score (string) from insights validation keys.
// The empty-state sentence gets a key per branch rather than interpolating the
// label, which no locale could inflect.
const INSIGHTS_VALIDATION_KEYS = [
  {
    key: 'supportingEvidence',
    labelKey: 'fourCsWidget.truths.supportingEvidence',
    emptyKey: 'fourCsWidget.truths.noSupportingEvidenceFound',
  },
  {
    key: 'redFlags',
    labelKey: 'fourCsWidget.truths.redFlags',
    emptyKey: 'fourCsWidget.truths.noRedFlagsFound',
  },
  {
    key: 'recommendations',
    labelKey: 'fourCsWidget.truths.recommendations',
    emptyKey: 'fourCsWidget.truths.noRecommendationsFound',
  },
] as const;

export const Truths = ({
  data,
  chatTitle,
  selectedTruths,
  setSelectedTruths,
}: {
  data: FourCsWidgetSchemaType;
  chatTitle: string;
  selectedTruths: Set<string>;
  setSelectedTruths: (selectedTruths: Set<string>) => void;
}) => {
  const { t } = useTranslation('analysis');

  const { scrollToTop } = useSidePanelScroll();
  useEffect(() => {
    scrollToTop();
  }, [scrollToTop]);

  const handleTruthToggle = (truth: string) => {
    const next = new Set(selectedTruths);
    if (next.has(truth)) next.delete(truth);
    else next.add(truth);
    setSelectedTruths(next);
  };

  return (
    <CenteredContent>
      <SectionWrapper compact>
        <div className="flex flex-col gap-2">
          <p className="text-heading-xl text-foreground">
            {t('fourCsWidget.truths.selectTheTruthsYouWant')}
          </p>
          <p className="my-6 text-body-lg text-muted-foreground">
            <Trans
              i18nKey="fourCsWidget.truths.pickTheTruthsYouWant"
              ns="analysis"
              components={{ br: <br /> }}
            />
          </p>
        </div>
      </SectionWrapper>
      {FOUR_CS_KEYS.map(({ key, label }) => (
        <SectionWrapper
          title={t('fourCsWidget.truths.sectionTitle', { label, title: chatTitle })}
          key={key}
        >
          <ul>
            {data.fourCs[key].truths.map((truth, index) => (
              <li key={`${key}-${index}`}>
                <BorderedBox className="flex flex-row gap-2 my-4">
                  <div className="grid place-items-center">
                    <Checkbox
                      checked={selectedTruths.has(truth.truth)}
                      onCheckedChange={() => handleTruthToggle(truth.truth)}
                      aria-label={truth.truth}
                    />
                  </div>
                  <div>
                    {truth.recommended ? (
                      <div className="mb-4">
                        <Badge variant="secondary">{t('fourCsWidget.truths.recommended')}</Badge>
                      </div>
                    ) : null}
                    <p className="text-body-lg text-foreground">{truth.truth}</p>
                  </div>
                </BorderedBox>
              </li>
            ))}
          </ul>
        </SectionWrapper>
      ))}
      <SectionWrapper title={t('fourCsWidget.truths.insightsValidation')}>
        <BorderedBox className="flex flex-row gap-2 bg-orange-50 mb-4 border-none">
          <div className="min-w-0 shrink-0">
            <OptimizedImage
              src={CheckCircleOrangeImage}
              alt={t('fourCsWidget.truths.checkCircle')}
              width={20}
              height={20}
            />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-body-lg font-medium text-orange-900">
              {t('fourCsWidget.truths.confidenceScore')}
            </p>
            <p className="text-body-lg text-foreground">{data.validation.confidenceScore}</p>
          </div>
        </BorderedBox>
        {INSIGHTS_VALIDATION_KEYS.map(({ key, labelKey, emptyKey }) => {
          const items = data.validation[key];
          return (
            <BorderedBox className="flex flex-row gap-2 mb-4" key={key}>
              <div className="min-w-0 shrink-0">
                <OptimizedImage
                  src={CheckCircleImage}
                  alt={t('fourCsWidget.truths.checkCircle')}
                  width={20}
                  height={20}
                />
              </div>
              <div>
                <p className="text-body-lg font-medium text-foreground">{t(labelKey)}:</p>
                {items.length === 0 ? (
                  <p className="text-body-lg font-medium text-foreground">{t(emptyKey)}</p>
                ) : (
                  <ul className="list-disc p-4 space-y-6.5">
                    {items.map((item: string, index: number) => (
                      <li key={index} className="list-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </BorderedBox>
          );
        })}
      </SectionWrapper>
    </CenteredContent>
  );
};
